import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import "@/styles/globals.css";
import "@/lib/i18n";  // inicializa i18next no primeiro import
import { DialogProvider } from "@/components/ui/DialogProvider";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";  // R19_ERROR_BOUNDARY_APPLIED
import { initSentry } from "@/lib/sentry";  // R51_SENTRY_FRONT

initSentry();  // no-op se não houver NEXT_PUBLIC_SENTRY_DSN (e só roda no cliente)

import RequireAuth from "@/components/RequireAuth";
import Layout from "@/components/Layout";
import { CommandPalette } from "@/components/CommandPalette";
import { CookieBanner } from "@/components/ui/CookieBanner";  // R24_COOKIE_BANNER_WIRED

// Rotas que NÃO usam Layout/RequireAuth (públicas)
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/signup",
  "/auth/callback",
  "/auth/recover",
];

// Rotas que PRECISAM de auth mas SEM o Layout (full-screen)
// Ex: /auth/primeira-senha — onde forçamos user novo a trocar senha
//     /escolher-empresa — onde user com 2+ empresas escolhe qual entrar
const SEMI_PUBLIC_PATHS = [
  "/auth/primeira-senha",
  "/escolher-empresa",
];

function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.some(p => path === p || path.startsWith(p + "?") || path.startsWith(p + "/"));
}

function isSemiPublicPath(path: string): boolean {
  return SEMI_PUBLIC_PATHS.some(p => path === p || path.startsWith(p + "?") || path.startsWith(p + "/"));
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  const isPublic = isPublicPath(router.pathname);
  const isSemiPublic = isSemiPublicPath(router.pathname);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <DialogProvider>
          {isPublic ? (
            <Component {...pageProps} />
          ) : isSemiPublic ? (
            <RequireAuth>
              <Component {...pageProps} />
            </RequireAuth>
          ) : (
            <RequireAuth>
              <Layout>
                <Component {...pageProps} />
              </Layout>
              {/* Cmd+K — só dentro de RequireAuth pra ter user logado */}
              <CommandPalette />
            </RequireAuth>
          )}
          <CookieBanner />
        </DialogProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
